<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import EditSheet from 'dashboard/components-next/polaris/cards/EditSheet.vue';
import FoldingGroup from 'dashboard/components-next/polaris/cards/FoldingGroup.vue';
import { FieldError } from 'dashboard/components-next/polaris/ui/field';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TeamForm from './TeamForm.vue';
import AgentSelector from './AgentSelector.vue';

// R2-9-6 (phase 9.3): creating or editing a team is one drawer over the team list, opened by the routes that were
// the wizard's (new, :teamId/edit). The steps become two sections, "Details" (TeamForm) and "Agents"
// (AgentSelector); the "Done" step becomes the success alert on save. Same store actions as the steps had:
// teams/create or teams/update, then teamMembers/create or teamMembers/update.
const props = defineProps({
  teamId: { type: [String, Number], default: null },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const agentList = useMapGetter('agents/getAgents');
const teamsUIFlags = useMapGetter('teams/getUIFlags');

const sheetRef = ref(null);
const formRef = ref(null);
const selectedAgents = ref([]);
const agentsTouched = ref(false);
const isSaving = ref(false);
const isLoadingMembers = ref(!!props.teamId);
const snapshot = ref('');

const isEdit = computed(() => !!props.teamId);
const team = computed(() =>
  isEdit.value ? store.getters['teams/getTeam'](props.teamId) : {}
);
const showForm = computed(
  () => !isEdit.value || (!!team.value?.id && !teamsUIFlags.value.isFetching)
);

const formJSON = () =>
  JSON.stringify([formRef.value?.state ?? null, selectedAgents.value]);
const takeSnapshot = () =>
  nextTick(() => {
    snapshot.value = formJSON();
  });
const dirty = computed(() => formJSON() !== snapshot.value);

const agentsError = computed(
  () => agentsTouched.value && !selectedAgents.value.length
);

const updateSelectedAgents = list => {
  agentsTouched.value = true;
  selectedAgents.value = [...list];
};

onMounted(async () => {
  store.dispatch('agents/get');
  if (isEdit.value) {
    try {
      await store.dispatch('teamMembers/get', { teamId: props.teamId });
      selectedAgents.value = store.getters['teamMembers/getTeamMembers'](
        props.teamId
      ).map(item => item.id);
    } catch {
      selectedAgents.value = [];
    } finally {
      isLoadingMembers.value = false;
    }
  }
});

// The original is what the form shows once the team and its members are in.
watch(
  () => showForm.value && !isLoadingMembers.value,
  ready => {
    if (ready) takeSnapshot();
  },
  { immediate: true }
);

const close = () => router.push({ name: 'settings_teams_list' });

// A new team created while adding its agents failed: a second Save updates that team instead of creating another
// (the old wizard moved on to the agents step of the team it had just created).
const createdTeamId = ref(null);

const saveTeam = async data => {
  agentsTouched.value = true;
  if (!selectedAgents.value.length) return;
  isSaving.value = true;
  try {
    let teamId = props.teamId || createdTeamId.value;
    if (teamId) {
      await store.dispatch('teams/update', { id: teamId, ...data });
    } else {
      const created = await store.dispatch('teams/create', { ...data });
      teamId = created.id;
      createdTeamId.value = teamId;
    }
    await store.dispatch(
      isEdit.value ? 'teamMembers/update' : 'teamMembers/create',
      { teamId, agentsList: selectedAgents.value }
    );
    store.dispatch('teams/get');
    useAlert(t('TEAMS_SETTINGS.FINISH.MESSAGE'));
    snapshot.value = formJSON();
    close();
  } catch (error) {
    useAlert(error?.message || t('TEAMS_SETTINGS.TEAM_FORM.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
  }
};

const title = computed(() =>
  isEdit.value
    ? t('TEAMS_SETTINGS.EDIT_FLOW.CREATE.TITLE')
    : t('TEAMS_SETTINGS.CREATE_FLOW.CREATE.TITLE')
);
const description = computed(() =>
  isEdit.value
    ? t('TEAMS_SETTINGS.EDIT_FLOW.CREATE.DESC')
    : t('TEAMS_SETTINGS.CREATE_FLOW.CREATE.DESC')
);
const saveLabel = computed(() =>
  isEdit.value
    ? t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.BUTTON_TEXT')
    : t('TEAMS_SETTINGS.FORM.SUBMIT_CREATE')
);
const scope = computed(
  () => `${t('SIDEBAR.SETTINGS')} · ${t('TEAMS_SETTINGS.HEADER')}`
);

const onOpenChange = value => {
  if (!value) close();
};

const runGuarded = action =>
  sheetRef.value ? sheetRef.value.runGuarded(action) : action();
defineExpose({ runGuarded });
</script>

<template>
  <EditSheet
    ref="sheetRef"
    open
    :title="title"
    :scope="scope"
    :dirty="dirty"
    :save-label="saveLabel"
    :can-save="showForm && !isSaving"
    @update:open="onOpenChange"
    @save="formRef?.handleSubmit()"
  >
    <p class="type-polaris-body text-polaris-muted-foreground">
      {{ description }}
    </p>
    <FoldingGroup :title="t('SETTINGS_PIECES.SECTIONS.DETAILS')">
      <TeamForm
        v-if="showForm"
        ref="formRef"
        :form-data="team"
        :on-submit="saveTeam"
      />
      <Spinner v-else :size="16" />
    </FoldingGroup>
    <FoldingGroup
      :title="t('SETTINGS_PIECES.SECTIONS.AGENTS')"
      :count="selectedAgents.length"
    >
      <div class="grid gap-2">
        <FieldError v-if="agentsError">
          {{ t('TEAMS_SETTINGS.ADD.AGENT_VALIDATION_ERROR') }}
        </FieldError>
        <Spinner v-if="isLoadingMembers" :size="16" />
        <AgentSelector
          v-else
          :agent-list="agentList"
          :selected-agents="selectedAgents"
          :update-selected-agents="updateSelectedAgents"
        />
      </div>
    </FoldingGroup>
  </EditSheet>
</template>
